/**
 * カード一覧の並べ方（Google Keep 風のマソンリー配置）。DOM に依存しない純粋関数だけを置く。
 */

export interface MasonryOptions {
  /** 一覧の横幅(px) */
  boardWidth: number;
  /** 1列の最小幅(px)。これ以上を保てる範囲で、できるだけ多く列を作る */
  minColumnWidth: number;
  /** カード同士の隙間(px) */
  gap: number;
}

export interface MasonryPlacement {
  x: number;
  y: number;
  column: number;
}

export interface MasonryLayout {
  columnCount: number;
  columnWidth: number;
  placements: MasonryPlacement[];
  /** 一覧全体の高さ(px)。カードが無いときは 0 */
  height: number;
}

/** 横幅に収まる列数。最低でも1列にする。 */
export function masonryColumnCount(boardWidth: number, minColumnWidth: number, gap: number): number {
  if (!(boardWidth > 0) || !(minColumnWidth > 0)) {
    return 1;
  }
  return Math.max(1, Math.floor((boardWidth + gap) / (minColumnWidth + gap)));
}

/**
 * 各カードの高さから位置を決める。カードは並び順に、いま一番低い列へ積む。
 * 同じ高さの列が複数あるときは左の列を選ぶので、高さがそろっていれば左から右へ並ぶ。
 * 並び順とアウトラインの順序は一致したまま（DOM の順序を変えない）。
 */
export function layoutMasonry(heights: number[], options: MasonryOptions): MasonryLayout {
  const { boardWidth, minColumnWidth, gap } = options;
  const columnCount = masonryColumnCount(boardWidth, minColumnWidth, gap);
  const columnWidth = Math.max(0, (boardWidth - gap * (columnCount - 1)) / columnCount);
  const columnBottoms: number[] = new Array(columnCount).fill(0);
  const placements: MasonryPlacement[] = [];

  for (const rawHeight of heights) {
    const height = Math.max(0, rawHeight);
    let column = 0;
    for (let i = 1; i < columnCount; i += 1) {
      // 高さの誤差(サブピクセル)で列が入れ替わらないよう、小さな差は左を優先する
      if (columnBottoms[i] < columnBottoms[column] - 0.5) {
        column = i;
      }
    }
    const y = columnBottoms[column];
    placements.push({ x: column * (columnWidth + gap), y, column });
    columnBottoms[column] = y + height + gap;
  }

  const tallest = Math.max(0, ...columnBottoms);
  return {
    columnCount,
    columnWidth,
    placements,
    height: heights.length > 0 ? Math.max(0, tallest - gap) : 0,
  };
}

/**
 * 持ち上げたカードをどこへ差し込むかを決める。
 * `otherHeights` は持ち上げたカードを除いた並び。差し込み位置ごとに配置を計算し、
 * 差し込んだときの枠の中心が、いまカードの中心がある場所に一番近いものを選ぶ。
 *
 * 差し込むと他のカードの位置が動くため、境目では選び先が行ったり来たりしやすい。
 * `currentIndex` からの乗り換えは、`hysteresis`(px) 以上近づくときだけ認める。
 */
export function chooseInsertIndex(
  otherHeights: number[],
  draggedHeight: number,
  center: { x: number; y: number },
  options: MasonryOptions,
  currentIndex: number | null = null,
  hysteresis = 16
): number {
  let bestIndex = 0;
  let bestDistance = Infinity;
  let currentDistance = Infinity;

  for (let index = 0; index <= otherHeights.length; index += 1) {
    const heights = [...otherHeights.slice(0, index), draggedHeight, ...otherHeights.slice(index)];
    const layout = layoutMasonry(heights, options);
    const placement = layout.placements[index];
    const distance = Math.hypot(
      placement.x + layout.columnWidth / 2 - center.x,
      placement.y + draggedHeight / 2 - center.y
    );
    if (index === currentIndex) {
      currentDistance = distance;
    }
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  }

  if (currentIndex !== null && currentIndex >= 0 && currentIndex <= otherHeights.length) {
    return bestDistance < currentDistance - hysteresis ? bestIndex : currentIndex;
  }
  return bestIndex;
}
